import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateStep } from 'redux/slices/createTrial';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { placePlotsSchema } from 'validators/createTrialSchemas';
import { useCreateTrialMutation } from 'redux/services/workspace';
import { useGuestCreateTrialMutation } from 'redux/services/guest';
import { createTrialSteps, guestCreateTrialSteps } from 'utils/createTrial';
import AuthFooterRedirect from 'components/auth/AuthFormWrapper/subcomponents/AuthFooterRedirect';
import useAuth from 'hooks/useAuth';
import useStorage from 'hooks/useStorage';
import saveFile from 'utils/helpers/saveFile';
import Button from 'components/global/Button';
import PurchaseShapefile from './PurchaseShapefile';
import splitString from 'utils/helpers/splitString';
import getErrorMessage from 'utils/helpers/getErrorMessage';
import PlacePlotsForm from 'components/create-trial/PlacePlotsForm';
import toast from 'react-hot-toast';
import { Routes } from 'types/global.type';

import { StepProps } from '../../types/stepFlow.type';

const PlacePlotsContainer = ({ children }: StepProps) => {
  const { trial } = useAppSelector((state) => state.createTrial);
  const [previewUrl, setPreviewUrl] = useState('');
  const [planningSummaryUrl, setPlanningSummaryUrl] = useState('');
  const [lsdData, setLsdData] = useState('');
  const [plotRandomizationSeed, setPlotRandomizationSeed] = useState<
    string | number | null
  >(null);
  const [userWarnings, setUserWarnings] = useState<string[]>([]);
  const [
    createTrial,
    { data: savedTrial, isLoading: trialLoading, error: trialError },
  ] = useCreateTrialMutation();
  const [
    previewTrial,
    {
      data: trialPreview,
      isLoading: trialPreviewLoading,
      error: trialPreviewError,
    },
  ] = useGuestCreateTrialMutation();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const { addStorageValue, removeStorageValue } = useStorage();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (savedTrial) {
      setIsImageLoading(true);
      setPreviewUrl(savedTrial.previewUrl);
      setPlanningSummaryUrl(savedTrial.data.plan_summary_file_url || '');

      // Capture the plot randomization seed if it exists in the response
      if (
        savedTrial.data &&
        'plot_randomization_seed' in (savedTrial.data as any)
      ) {
        setPlotRandomizationSeed(
          (savedTrial.data as any).plot_randomization_seed,
        );
      }

      // Extract user warnings if they exist
      if (savedTrial.data && (savedTrial.data as any).userWarnings) {
        setUserWarnings((savedTrial.data as any).userWarnings);
      } else {
        setUserWarnings([]);
      }

      addStorageValue('createTrialResponse', savedTrial, 'session');
    }
  }, [savedTrial, trialError, addStorageValue]);

  useEffect(() => {
    if (trialPreview) {
      setIsImageLoading(true);
      setPreviewUrl(trialPreview.previewUrl);
      setPlanningSummaryUrl(trialPreview.planningSummaryUrl);

      // Capture the plot randomization seed if it exists in the response
      if (
        trialPreview.data &&
        'plot_randomization_seed' in (trialPreview.data as any)
      ) {
        setPlotRandomizationSeed(
          (trialPreview.data as any).plot_randomization_seed,
        );
      }

      // Extract user warnings if they exist
      if (trialPreview.data && (trialPreview.data as any).userWarnings) {
        setUserWarnings((trialPreview.data as any).userWarnings);
      } else {
        setUserWarnings([]);
      }
    }
  }, [trialPreview]);

  const getJsonData = (values: Record<string, any>): Record<string, any> => {
    const jsonData: Record<string, any> = {};
    values.seed = 1;
    values.control_index = 0;
    values.offset = 0;

    // Include the plot randomization seed if it exists
    if (plotRandomizationSeed !== null) {
      jsonData.plot_randomization_seed = plotRandomizationSeed;
    }

    if (!isLoggedIn) {
      values.product = 'Test product';
      values.field_name = 'Test field';
    }

    const defaultFormKeys = [
      'business_name',
      'farm_name',
      'product',
      'crop',
      'variety',
      'length_ft',
      'width_ft',
      'buffer_ft',
      'seed',
      'field_name',
      'application_units',
      'nominal_rate',
      'nominal_label',
      'control_index',
      'is_microbe_test',
      'start_date',
      'end_date',
      'offset',
      'planning_trial_type',
      'planning_trial_subtype',
      'apply_trial_buffer_to_field_boundary',
      'trial_equipment',
      'reps_to_remove_from_design',
      'abline_method',
      'abline_heading',
      'abline_adjust',
      'abline_rotation',
      'abline_files_meta',
      'field_boundary_files_meta',
      'trial_boundary_files_meta',
      'treatment_drift_units',
      'max_likely_harvest_angle',
      'plot_buffer_width',
      'plot_buffer_length',
      'execution_equipment_swath_width',
      'execution_equipment_swath_width_units',
      'execution_equipment_rate_change_distance',
      'execution_equipment_rate_change_distance_units',
      'execution_equipment_can_split_swath',
      'execution_equipment_dgps_type',
      'execution_equipment_dgps_accuracy',
      'execution_equipment_dgps_units',
      'harvester_swath_width',
      'harvester_swath_width_units',
      'harvester_spatial_resolution',
      'harvester_spatial_resolution_units',
      'harvester_dgps_type',
      'harvester_dgps_accuracy',
      'harvester_dgps_units',
    ];

    // GeoJSON objects that should not be stringified
    const geoJsonKeys = [
      'field_boundary_geojson',
      'trial_boundary_geojson',
      'abline_geojson',
    ];

    const percentKeys = ['nominal_management_prop', 'prop_keep'];

    for (const key of defaultFormKeys) {
      if (values[key] !== undefined && values[key] !== null) {
        // Preserve numbers, booleans, arrays and objects
        jsonData[key] = values[key];
      }
    }

    // Always set plot_buffer_units to 'ft' for now
    jsonData.plot_buffer_units = 'ft';

    // Currently we treat drift as the same in both directions, but the backend supports different values for each direction
    jsonData.treatment_drift_width = values.treatment_drift_distance;
    jsonData.treatment_drift_length = values.treatment_drift_distance;

    // Handle GeoJSON objects properly
    for (const key of geoJsonKeys) {
      if (values[key]) {
        jsonData[key] = values[key]; // Pass the object directly, don't stringify
      }
    }

    for (const key of percentKeys) {
      // Convert percentages to decimals but keep as numbers
      jsonData[key] = Number(values[key]) / 100;
    }

    if (values.test_nutrients?.length) {
      jsonData.test_nutrients = values.test_nutrients.join(',');
    }

    jsonData.trial_type = `${values.trial_type}s`;

    // Handle arrays or comma-separated strings for treatment rates
    if (Array.isArray(values.treatment_rates)) {
      jsonData.treatment_rates = values.treatment_rates;
    } else if (typeof values.treatment_rates === 'string') {
      jsonData.treatment_rates = splitString(values.treatment_rates, ', ').join(
        ',',
      );
    }

    // Handle arrays or comma-separated strings for treatment labels
    if (values.treatment_labels && values.treatment_labels !== 'Use rates') {
      if (Array.isArray(values.treatment_labels)) {
        jsonData.treatment_labels = values.treatment_labels;
      } else if (typeof values.treatment_labels === 'string') {
        jsonData.treatment_labels = splitString(
          values.treatment_labels,
          ', ',
        ).join(',');
      }
    } else {
      jsonData.treatment_labels = 'Use rates';
    }

    return jsonData;
  };

  const updateLocalTrial = (values: Record<string, any>) => {
    dispatch(
      updateStep({
        index:
          (isLoggedIn ? createTrialSteps : guestCreateTrialSteps).length - 1,
        data: values,
      }),
    );
  };

  return (
    <Formik
      initialValues={{
        prop_keep: '',
        nominal_management_prop: '',
        lsd: '',
      }}
      onSubmit={(values) => {
        updateLocalTrial(values);
        previewTrial(getJsonData({ ...trial, ...values })).then((response) => {
          if ('data' in response) {
            const lsdPercent = response.data.lsdPercent;
            setLsdData(String(lsdPercent));

            // Capture the plot randomization seed from the response
            if ('plot_randomization_seed' in (response.data as any)) {
              setPlotRandomizationSeed(
                (response.data as any).plot_randomization_seed,
              );
            }

            // Extract user warnings from the immediate response
            if ((response.data as any).userWarnings) {
              setUserWarnings((response.data as any).userWarnings);
            } else {
              setUserWarnings([]);
            }
          }
        });
      }}
      validationSchema={placePlotsSchema}
      validateOnMount
    >
      {({ values }) => (
        <>
          <PlacePlotsForm
            error={getErrorMessage(trialPreviewError || trialError)}
            isLoading={trialPreviewLoading || isImageLoading}
            imageUrl={previewUrl}
            updatedLsdData={lsdData}
            onImageLoad={() => setIsImageLoading(false)}
            isSummaryAvailable={planningSummaryUrl !== ''}
            userWarnings={userWarnings}
          >
            <PurchaseShapefile
              getTrialData={() => {
                updateLocalTrial(values);
                return getJsonData({ ...trial, ...values });
              }}
              disablePurchase={
                values.nominal_management_prop === '' || values.prop_keep === ''
              }
            />

            <Button
              action="button"
              variant="brand"
              isDisabled={planningSummaryUrl === ''}
              onClick={() => {
                if (isLoggedIn && !savedTrial?.data?.plan_summary_file_url) {
                  updateLocalTrial(values);
                  createTrial(getJsonData({ ...trial, ...values }));
                }
                if (planningSummaryUrl?.match(/^(http|https):\/\/[^ "]+$/)) {
                  saveFile(planningSummaryUrl);
                } else {
                  const errorMessage =
                    'We could not generate the planning summary for this trial. Please contact us for further assistance.';
                  toast.loading(errorMessage, {
                    duration: 5000,
                  });
                }
              }}
              isLoading={Boolean(trialLoading && isLoggedIn)}
            >
              View Summary
            </Button>

            <Button
              action="button"
              variant="brand"
              onClick={() => {
                if (savedTrial?.previewUrl || !isLoggedIn) {
                  removeStorageValue('createTrialResponse', 'session');
                  navigate(0);
                } else {
                  updateLocalTrial(values);
                  createTrial(getJsonData({ ...trial, ...values }));
                }
              }}
              isLoading={trialLoading}
            >
              {savedTrial?.previewUrl || !isLoggedIn ? 'Create new' : 'Save'}
            </Button>
            {!isLoggedIn && trialPreview?.planningSummaryUrl && (
              <div style={{ width: '100%', fontWeight: 'bolder' }}>
                <AuthFooterRedirect
                  {...{
                    description:
                      'If you like your trial shapefile and want to purchase it, first',
                    cta: 'create an account',
                    path: Routes.signUp,
                  }}
                />
              </div>
            )}
          </PlacePlotsForm>
          {children}
        </>
      )}
    </Formik>
  );
};

export default PlacePlotsContainer;
